/**
 * PollsPage - List polls for a trip, create new ones
 * Route: /trips/:id/polls
 * Design: tripways.pen node J9fP1/NskbT (desktop list/empty) / caUmC/PEuIk (mobile list/empty)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, Compass, Plus, Vote } from 'lucide-react';
import { apiGet } from '../services/apiClient';
import { useToast } from '../contexts/ToastContext';
import { usePolls } from '../hooks/usePolls';
import { PollApiError } from '../services/pollService';
import DesktopSidebar from '../components/common/DesktopSidebar';
import PollCard from '../components/polls/PollCard';
import CreatePollModal from '../components/polls/CreatePollModal';
import type { TripDetailResponse } from '../types/trip';

export default function PollsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [tripName, setTripName] = useState('');
  const { polls, loading, createPoll } = usePolls(id);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!id) return;
    apiGet<TripDetailResponse>(`/trip/${id}`).then((response) => {
      if (response.exitCode === 1 && response.data) setTripName(response.data.name);
    });
  }, [id]);

  if (!id) return null;

  const openPolls = polls.filter((p) => p.status === 'OPEN');
  const closedPolls = polls.filter((p) => p.status === 'CLOSED');

  const handleCreate = async (input: Parameters<typeof createPoll>[0]) => {
    try {
      await createPoll(input);
      showToast('success', '已建立投票');
    } catch (err) {
      showToast('error', err instanceof PollApiError ? err.message : '建立投票失敗');
      throw err;
    }
  };

  const goToDetail = (pollId: number | string) => navigate(`/trips/${id}/polls/${pollId}`);

  const emptyState = (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16 px-6 text-center">
      <div className="w-[72px] h-[72px] rounded-full bg-[#f0f9fe] flex items-center justify-center">
        <Vote size={32} className="text-primary" />
      </div>
      <h2 className="text-slate-900 text-lg font-semibold">還沒有任何投票</h2>
      <p className="text-slate-400 text-sm w-[300px]">發起第一個投票，讓旅伴一起決定行程中的大小事。</p>
      <button
        onClick={() => setShowCreate(true)}
        className="flex items-center gap-2 rounded-full bg-primary text-white text-sm font-semibold px-6 py-3"
      >
        <Plus size={16} />
        發起第一個投票
      </button>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#f5f8f8]">
      <DesktopSidebar onNavigate={navigate} tripId={id} />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ─── Mobile Header ─── */}
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-5 py-4">
            <div className="flex items-center gap-1">
              <button onClick={() => navigate(-1)} className="flex items-center justify-center w-11 h-11 -ml-2">
                <ChevronLeft size={24} className="text-slate-900" />
              </button>
              <h1 className="text-slate-900 text-[17px] font-bold">投票</h1>
            </div>
            <button onClick={() => setShowCreate(true)} className="flex items-center justify-center w-11 h-11">
              <Plus size={22} className="text-primary" />
            </button>
          </div>
          {polls.length > 0 && (
            <div className="flex items-center justify-between px-5 pb-3">
              <span className="text-sm font-medium text-slate-400">{polls.length} 個投票</span>
              <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-500">
                <Compass size={12} />
                {tripName}
              </span>
            </div>
          )}
        </header>

        {/* ─── Desktop Top Bar ─── */}
        <header className="hidden lg:flex items-center justify-between bg-white border-b border-slate-100 px-8 py-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 rounded-[10px] border border-slate-200 px-3 py-2 text-[13px] font-medium text-slate-900"
            >
              <ArrowLeft size={16} />
              返回
            </button>
            <h1 className="text-slate-900 text-xl font-bold">投票</h1>
            {tripName && (
              <span className="flex items-center gap-1.5 rounded-full bg-slate-100 px-3.5 py-1.5 text-xs font-medium text-slate-500">
                <Compass size={13} />
                {tripName}
              </span>
            )}
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 h-10 px-5 rounded-full bg-primary text-white text-sm font-semibold"
          >
            <Plus size={16} />
            發起投票
          </button>
        </header>

        {/* ─── Main ─── */}
        {!loading && polls.length === 0 ? (
          emptyState
        ) : (
          <main className="flex-1 px-4 lg:px-10 py-5 lg:py-8">
            <div className="max-w-3xl mx-auto flex flex-col gap-6">
              {openPolls.length > 0 && (
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs font-semibold text-slate-400">進行中</span>
                  <div className="flex flex-col gap-2.5">
                    {openPolls.map((poll) => (
                      <PollCard key={poll.id} poll={poll} onClick={() => goToDetail(poll.id)} />
                    ))}
                  </div>
                </div>
              )}
              {closedPolls.length > 0 && (
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs font-semibold text-slate-400">已結束</span>
                  <div className="flex flex-col gap-2.5">
                    {closedPolls.map((poll) => (
                      <PollCard key={poll.id} poll={poll} onClick={() => goToDetail(poll.id)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </main>
        )}
      </div>

      <CreatePollModal isOpen={showCreate} onClose={() => setShowCreate(false)} tripId={id} onSubmit={handleCreate} />
    </div>
  );
}
